import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';

const W = 480;
const H = 420;

export interface SpaceInvadersModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Minimal Space Invaders clone for the dashboard easter egg.
 * Arrows / A,D to move, Space or ↑ to shoot. Touch: left third / right third to move, middle to fire.
 */
export const SpaceInvadersModal: React.FC<SpaceInvadersModalProps> = ({ open, onClose }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const keysRef = useRef<Record<string, boolean>>({});
  const [restartToken, setRestartToken] = useState(0);

  useEffect(() => {
    if (!open) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctxMaybe = canvas.getContext('2d');
    if (!ctxMaybe) return;
    const ctx: CanvasRenderingContext2D = ctxMaybe;

    type Invader = { x: number; y: number; alive: boolean };
    type Bullet = { x: number; y: number; vy: number; fromPlayer: boolean };

    const invCols = 11;
    const invRows = 5;
    const invW = 26;
    const invH = 18;
    const gapX = 10;
    const gapY = 12;
    const playerW = 42;
    const playerH = 14;
    const playerSpeed = 5;
    const bulletSpeed = 9;
    const invaderBulletSpeed = 4;

    let invaders: Invader[] = [];
    let bullets: Bullet[] = [];
    let playerX = W / 2 - playerW / 2;
    const playerY = H - 36;
    let invaderVx = 1.2;
    let score = 0;
    let frame = 0;
    let shootCooldown = 0;
    let invaderShootCooldown = 80;
    let status: 'playing' | 'won' | 'lost' = 'playing';

    const stars: { x: number; y: number; r: number }[] = [];
    for (let i = 0; i < 45; i++) {
      stars.push({ x: Math.random() * W, y: Math.random() * H, r: Math.random() * 1.2 + 0.3 });
    }

    function resetGame() {
      invaders = [];
      const startX = 36;
      const startY = 56;
      for (let r = 0; r < invRows; r++) {
        for (let c = 0; c < invCols; c++) {
          invaders.push({
            x: startX + c * (invW + gapX),
            y: startY + r * (invH + gapY),
            alive: true,
          });
        }
      }
      bullets = [];
      playerX = W / 2 - playerW / 2;
      invaderVx = 1.2;
      score = 0;
      frame = 0;
      shootCooldown = 0;
      invaderShootCooldown = 80;
      status = 'playing';
    }

    resetGame();

    const onKeyDown = (e: KeyboardEvent) => {
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', ' ', 'KeyA', 'KeyD'].includes(e.code)) {
        e.preventDefault();
      }
      keysRef.current[e.code] = true;
      if (e.code === 'Space' && status === 'playing') {
        tryShoot();
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      keysRef.current[e.code] = false;
    };

    function tryShoot() {
      if (shootCooldown > 0 || status !== 'playing') return;
      const alivePlayerBullets = bullets.filter((b) => b.fromPlayer).length;
      if (alivePlayerBullets >= 2) return;
      bullets.push({
        x: playerX + playerW / 2 - 2,
        y: playerY,
        vy: -bulletSpeed,
        fromPlayer: true,
      });
      shootCooldown = 18;
    }

    function aliveInvaders(): Invader[] {
      return invaders.filter((i) => i.alive);
    }

    function update() {
      if (status !== 'playing') return;
      frame++;

      if (keysRef.current['ArrowLeft'] || keysRef.current['KeyA']) {
        playerX = Math.max(8, playerX - playerSpeed);
      }
      if (keysRef.current['ArrowRight'] || keysRef.current['KeyD']) {
        playerX = Math.min(W - playerW - 8, playerX + playerSpeed);
      }
      if (keysRef.current['ArrowUp']) {
        tryShoot();
      }

      if (shootCooldown > 0) shootCooldown--;

      const alive = aliveInvaders();
      if (!alive.length) {
        status = 'won';
        return;
      }

      let minX = Infinity;
      let maxX = -Infinity;
      for (const inv of alive) {
        minX = Math.min(minX, inv.x);
        maxX = Math.max(maxX, inv.x + invW);
      }
      let stepDown = false;
      if ((maxX >= W - 12 && invaderVx > 0) || (minX <= 12 && invaderVx < 0)) {
        invaderVx *= -1;
        stepDown = true;
      }
      const dx = stepDown ? 0 : invaderVx;
      const dy = stepDown ? 10 : 0;
      for (const inv of invaders) {
        if (!inv.alive) continue;
        inv.x += dx;
        inv.y += dy;
        if (inv.y + invH >= playerY) {
          status = 'lost';
          return;
        }
      }

      invaderShootCooldown--;
      if (invaderShootCooldown <= 0 && alive.length) {
        invaderShootCooldown = 55 + Math.floor(Math.random() * 50);
        const shooter = alive[Math.floor(Math.random() * alive.length)];
        bullets.push({
          x: shooter.x + invW / 2 - 2,
          y: shooter.y + invH,
          vy: invaderBulletSpeed,
          fromPlayer: false,
        });
      }

      for (const b of bullets) {
        b.y += b.vy;
      }
      bullets = bullets.filter((b) => b.y > -20 && b.y < H + 20);

      for (const b of bullets) {
        if (!b.fromPlayer) {
          if (
            b.x < playerX + playerW &&
            b.x + 4 > playerX &&
            b.y < playerY + playerH &&
            b.y + 10 > playerY
          ) {
            status = 'lost';
            return;
          }
        } else {
          for (const inv of invaders) {
            if (
              !inv.alive ||
              b.x + 4 < inv.x ||
              b.x > inv.x + invW ||
              b.y + 10 < inv.y ||
              b.y > inv.y + invH
            ) {
              continue;
            }
            inv.alive = false;
            b.y = -999;
            score += 10;
            break;
          }
        }
      }
      bullets = bullets.filter((b) => b.y > -10);
    }

    function draw() {
      ctx.fillStyle = '#070714';
      ctx.fillRect(0, 0, W, H);
      for (const s of stars) {
        ctx.fillStyle = 'rgba(220,230,255,0.35)';
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();
      }

      for (const inv of invaders) {
        if (!inv.alive) continue;
        ctx.fillStyle = '#4ade80';
        ctx.fillRect(inv.x, inv.y, invW, invH);
        ctx.fillStyle = '#166534';
        ctx.fillRect(inv.x + 6, inv.y + 5, 6, 6);
        ctx.fillRect(inv.x + invW - 12, inv.y + 5, 6, 6);
      }

      ctx.fillStyle = '#38bdf8';
      ctx.fillRect(playerX, playerY, playerW, playerH);
      ctx.fillStyle = '#0ea5e9';
      ctx.fillRect(playerX + playerW / 2 - 6, playerY - 6, 12, 6);

      for (const b of bullets) {
        ctx.fillStyle = b.fromPlayer ? '#fde047' : '#f97316';
        ctx.fillRect(b.x, b.y, 4, 10);
      }

      ctx.fillStyle = '#e2e8f0';
      ctx.font = '600 14px system-ui, sans-serif';
      ctx.fillText(`Score ${score}`, 12, 24);

      if (status === 'won') {
        ctx.fillStyle = 'rgba(15, 23, 42, 0.82)';
        ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = '#86efac';
        ctx.font = '700 22px system-ui, sans-serif';
        ctx.fillText('You cleared the sector!', W / 2 - 130, H / 2 - 10);
        ctx.fillStyle = '#cbd5e1';
        ctx.font = '14px system-ui, sans-serif';
        ctx.fillText('Safety briefing: optional. Bragging: encouraged.', W / 2 - 175, H / 2 + 18);
      } else if (status === 'lost') {
        ctx.fillStyle = 'rgba(15, 23, 42, 0.82)';
        ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = '#fca5a5';
        ctx.font = '700 22px system-ui, sans-serif';
        ctx.fillText('Hull breach!', W / 2 - 70, H / 2 - 10);
        ctx.fillStyle = '#cbd5e1';
        ctx.font = '14px system-ui, sans-serif';
        ctx.fillText('Press R or click Restart.', W / 2 - 95, H / 2 + 18);
      }
    }

    let rafId = 0;
    let cancelled = false;

    const loop = () => {
      if (cancelled) return;
      update();
      draw();
      rafId = requestAnimationFrame(loop);
    };

    const onRestartKey = (e: KeyboardEvent) => {
      if (e.code === 'KeyR' && (status === 'won' || status === 'lost')) {
        e.preventDefault();
        resetGame();
      }
    };

    const onTouchStart = (e: TouchEvent) => {
      if (status !== 'playing') return;
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const tx = e.touches[0].clientX - rect.left;
      const nx = (tx / rect.width) * W;
      if (nx < W / 3) playerX = Math.max(8, playerX - playerSpeed * 3);
      else if (nx > (2 * W) / 3) playerX = Math.min(W - playerW - 8, playerX + playerSpeed * 3);
      else tryShoot();
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('keydown', onRestartKey);
    canvas.addEventListener('touchstart', onTouchStart, { passive: false });

    rafId = requestAnimationFrame(loop);

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('keydown', onRestartKey);
      canvas.removeEventListener('touchstart', onTouchStart);
      keysRef.current = {};
    };
  }, [open, restartToken]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
          border: '1px solid rgba(148, 163, 184, 0.25)',
        },
      }}
    >
      <DialogTitle sx={{ color: '#f8fafc', fontWeight: 700 }}>
        Unauthorized fun detected
      </DialogTitle>
      <DialogContent>
        <Typography
          variant="body2"
          sx={{
            color: 'rgba(226, 232, 240, 0.92)',
            mb: 2,
            fontStyle: 'italic',
            lineHeight: 1.6,
          }}
        >
          A bit cheeky, aren&apos;t we? That&apos;s the spirit—curiosity is half of safety culture. Here&apos;s your
          reward: five minutes of paperwork-free pew-pew.
        </Typography>
        <Box
          sx={{
            borderRadius: 1,
            overflow: 'hidden',
            border: '1px solid rgba(56, 189, 248, 0.35)',
            lineHeight: 0,
            mx: 'auto',
            maxWidth: W,
          }}
        >
          <canvas
            ref={canvasRef}
            width={W}
            height={H}
            style={{ width: '100%', height: 'auto', display: 'block', touchAction: 'none' }}
          />
        </Box>
        <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'rgba(148, 163, 184, 0.95)' }}>
          Move: ← → or A D · Shoot: Space or ↑ · Touch: sides to move, center to fire · After game over: R or Restart
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button variant="outlined" onClick={() => setRestartToken((t) => t + 1)} sx={{ borderColor: '#38bdf8', color: '#7dd3fc' }}>
          Restart game
        </Button>
        <Button onClick={onClose} sx={{ color: '#94a3b8', ml: 'auto' }}>
          Back to compliance
        </Button>
      </DialogActions>
    </Dialog>
  );
};
